/**
 * Pure helpers for the inventory-view "Transaction Inclusion" label rule.
 * Mirrors web-app-v3's src/util/labelOptions.ts so the two apps behave
 * identically. Kept framework-free so it can be unit-tested directly.
 */

export const TEMPLATE_OPTION_ID_PREFIX = 'template:';

export interface LabelRuleOption {
  /** Stable list key: the labelId for a flat label, `template:<prefix>` for a template. */
  id: string;
  name: string;
  kind: 'label' | 'template';
  /** What goes to the backend selector: the labelId (flat) or the prefix (template). */
  value: string;
}

export interface LabelInclusionRule {
  mode: 'include' | 'exclude';
  match: 'any' | 'all';
  selectors: { kind: 'label' | 'template'; value: string }[];
}

export interface LabelTrackingRule {
  /** The label template prefix, e.g. `client` (never a specific value like `client:001`). */
  template: string;
}

/**
 * The subset of options eligible as an inventory tracking dimension: label
 * templates only. A flat label has no values to partition by, and individual
 * slug values are never offered — only their template.
 */
export function buildLabelTemplateOptions(
  labelDefinitions: { labelId?: string; name?: string; enabled?: boolean; isDeleted?: boolean }[] | undefined
): LabelRuleOption[] {
  return buildLabelRuleOptions(labelDefinitions).filter((o) => o.kind === 'template');
}

/**
 * Unified picker list: every flat label plus the distinct templates of slugged
 * labels (e.g. `client:12345` → the `client` template). Individual slug values
 * are never shown — only their template. Flat labels first, then templates.
 */
export function buildLabelRuleOptions(
  labelDefinitions: { labelId?: string; name?: string; enabled?: boolean; isDeleted?: boolean }[] | undefined
): LabelRuleOption[] {
  const flat = new Map<string, LabelRuleOption>();
  const templates = new Map<string, LabelRuleOption>();

  for (const def of labelDefinitions ?? []) {
    // Skip disabled/deleted labels (mirrors web-app-v3 buildLabelOptions).
    if (!def?.enabled || def.isDeleted || !def.labelId) continue;
    const sep = def.labelId.indexOf(':');
    if (sep > 0) {
      const prefix = def.labelId.slice(0, sep);
      if (!templates.has(prefix)) {
        templates.set(prefix, {
          id: `${TEMPLATE_OPTION_ID_PREFIX}${prefix}`,
          name: prefix,
          kind: 'template',
          value: prefix,
        });
      }
    } else if (sep === -1 && !flat.has(def.labelId)) {
      flat.set(def.labelId, { id: def.labelId, name: def.name || def.labelId, kind: 'label', value: def.labelId });
    }
    // sep === 0 (leading colon) is malformed — skip it.
  }

  // Sort alphabetically (flat first, then templates) to match web-app-v3.
  const byName = (a: LabelRuleOption, b: LabelRuleOption) => a.name.localeCompare(b.name);
  return [...[...flat.values()].sort(byName), ...[...templates.values()].sort(byName)];
}

/**
 * Builds the persisted rule from the picker state, or undefined when there is
 * no filtering (mode 'all' or nothing selected).
 */
export function buildLabelInclusionRule(
  mode: 'all' | 'include' | 'exclude',
  match: 'any' | 'all',
  selectedIds: string[],
  options: LabelRuleOption[]
): LabelInclusionRule | undefined {
  if (mode === 'all' || selectedIds.length === 0) return undefined;

  const byId = new Map(options.map((o) => [o.id, o]));
  const selectors = selectedIds
    .map((id) => byId.get(id))
    .filter((o): o is LabelRuleOption => !!o)
    .map((o) => ({ kind: o.kind, value: o.value }));

  if (selectors.length === 0) return undefined;
  return { mode, match, selectors };
}

/**
 * Attaches the (create-time) label inclusion / tracking rules onto an inventory
 * view config payload, mutating and returning it. Each rule is attached only
 * when present, so an unset rule never adds an undefined key. Centralizes the
 * cast used until the generated api-svc client carries these fields, and makes
 * the integration seam (picker output -> create payload) unit-testable.
 */
export function applyLabelRulesToConfig<T extends object>(
  config: T,
  labelInclusionRule?: LabelInclusionRule,
  labelTrackingRule?: LabelTrackingRule
): T & { labelInclusionRule?: LabelInclusionRule; labelTrackingRule?: LabelTrackingRule } {
  const withRules = config as T & { labelInclusionRule?: LabelInclusionRule; labelTrackingRule?: LabelTrackingRule };
  if (labelInclusionRule) {
    withRules.labelInclusionRule = labelInclusionRule;
  }
  if (labelTrackingRule) {
    withRules.labelTrackingRule = labelTrackingRule;
  }
  return withRules;
}
